import React from 'react';
import BaseDialog from 'pages/Dashboard/components/Dialog';
import Button from 'pages/Dashboard/components/Button';
import MediaPreview from 'pages/Dashboard/components/MediaPreview';
import {
  useMedia,
} from 'core/hooks';
import Divider from '@mui/material/Divider';
import DeleteIcon from 'core/icons/delete';
import {
  useGetAllowedFileTypes,
} from 'pages/Dashboard/hooks/media';
import {
  MediaOriginatorType,
  MultimediaType,
} from 'pages/Dashboard/utils/constants';
import useDeleteMediaHandler from 'pages/Dashboard/hooks/useDeleteMediaHandler';
import Skeleton from 'core/components/Skeleton';
import isNil from 'lodash/isNil';
import useUploadProfilePicture from 'views/EMR/hooks/media';
import ImageUploadMenu from 'pages/Dashboard/pages/Charts/pages/Details/components/ImageUploadMenu';

export type ProfilePictureDialogProps = {
  entityId: number;
  originatorType?: MediaOriginatorType;
};

type Props = ProfilePictureDialogProps & {
  onClose: () => void;
};

export default function ProfilePictureDialog({
  onClose,
  entityId,
  originatorType = MediaOriginatorType.Patient,
}: Props) {

  const { getMediaById, isFetching } = useMedia();
  const media = getMediaById(entityId, originatorType);

  const {
    onMediaDelete,
    isPending: isDeletingMedia,
  } = useDeleteMediaHandler(entityId, originatorType);

  const {
    onUpdateProfilePicture,
    isPending: isUploadingMedia,
  } = useUploadProfilePicture(entityId, originatorType);

  const allowedFileTypes = useGetAllowedFileTypes(MultimediaType.ProfilePicture);

  const handleMediaDelete = () => {
    const { mediaId, fileName } = getMediaById(entityId, originatorType) ?? {};
    onMediaDelete(
      {
        list: [{
          originatorType,
          mediaId: mediaId ?? 0,
          fileName: fileName ?? '',
        }],
      },
    );
  };

  const isMediaFetching = isFetching(originatorType, entityId);
  const isLoading = isUploadingMedia || isMediaFetching || isDeletingMedia;
  const mediaNotAvailable = isNil(media);

  return (
    <BaseDialog
      title="Profile Picture"
      maxWidth={false}
      onClose={onClose}
      className="!w-[700px] overflow-hidden !p-0"
      titleClassName="p-5 pb-0"
      open
    >
      <div className="w-full overflow-y-auto !h-[400px] !bg-gray-100">
        {isLoading ? (
          <Skeleton className="!h-full !w-full !rounded-none" />
        ) : (
          <div className="w-full h-full">
            {mediaNotAvailable ? (
              <div className="w-full h-full flex items-center justify-center">
                <span className="font-semibold text-base">No profile picture uploaded</span>
              </div>
            ) : (
              <MediaPreview
                media={media ?? {}}
                classes={{ image: '!h-[400px]', root: '!bg-gray-100' }}
              />
            )}
          </div>
        )}
      </div>
      <div className="flex justify-between items-center gap-2 p-5 pt-0">
        <Button
          variant="outlined"
          type="button"
          onClick={() => { onClose(); }}
          disabled={isLoading}
        >
          Close
        </Button>
        <div className="flex gap-2">
          <Button
            variant="outlined"
            className="flex items-center gap-1 text-red-500"
            color="error"
            onClick={handleMediaDelete}
            disabled={isLoading || mediaNotAvailable}
          >
            <DeleteIcon className="!w-4 !h-4 !text-inherit" />
            Delete
          </Button>
          <Divider orientation="vertical" flexItem />
          <ImageUploadMenu
            onUpload={onUpdateProfilePicture}
            allowedFileTypes={allowedFileTypes}
            isUploading={isUploadingMedia}
            scanProps={{
              captureType: 'profile-picture',
              limit: 10,
              onCapturedFilesConfirmed: onUpdateProfilePicture,
              multiple: false,
            }}
          />
        </div>
      </div>
    </BaseDialog>
  );
}
